<template>
  <section :class="sectionClasses">
    <div :class="layoutClasses">
      <div v-if="showSlot">
        <slot />
      </div>

      <aside class="relative" :class="panelClasses" ref="widget">
        <a :id="widgetId" class="absolute -top-36 lg:-top-8 left-0"></a>
        <twist v-if="layout === 'split'" class="hidden lg:block w-[346px] h-[167px] absolute -top-12 -right-24 rotate-[35.6deg] text-default"></twist>

        <div :class="headerClasses">
          <svg width="32" height="32" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M2.40667 13.4556L20.5932 2.95557C22.014 2.13525 23.871 2.63282 24.6913 4.05364L26.121 6.52993C26.3553 6.93588 26.2821 7.4653 25.9871 7.79802C25.3798 8.5275 25.274 9.5629 25.7427 10.3748C26.2349 11.2273 27.1845 11.6533 28.1199 11.4922C28.5556 11.403 29.0507 11.6043 29.285 12.0103L30.6913 14.4459C31.535 15.9074 31.014 17.7237 29.5932 18.544L11.4067 29.044C9.94525 29.8878 8.15234 29.4074 7.30859 27.9459L5.90234 25.5103C5.66797 25.1043 5.7006 24.5983 6.03624 24.2422C6.60288 23.5361 6.74934 22.4773 6.25715 21.6248C5.7884 20.8129 4.7982 20.4103 3.90343 20.548C3.42716 20.6606 2.97266 20.4359 2.73828 20.0299L1.30859 17.5536C0.488281 16.1328 0.945252 14.2993 2.40667 13.4556ZM3.25715 16.4286L4.31184 18.2554C5.84693 18.2893 7.3854 19.079 8.20571 20.4998C9.04946 21.9612 8.96412 23.6884 8.2259 25.0348L9.25715 26.8209C9.46809 27.1863 9.91632 27.3064 10.2817 27.0955L28.4682 16.5955C28.793 16.408 28.9537 15.9363 28.7427 15.5709L27.7115 13.7848C26.1358 13.7743 24.6379 12.9612 23.7942 11.4998C22.9739 10.079 23.0186 8.37522 23.7974 7.00541L22.7427 5.17864C22.5552 4.85388 22.043 4.71662 21.7182 4.90412L3.53167 15.4041C3.16632 15.6151 3.06965 16.1039 3.25715 16.4286ZM21.6716 15.3234L19.4216 11.4262L10.3283 16.6762L12.5783 20.5734L21.6716 15.3234ZM8.00475 17.1517C7.60631 16.4616 7.82308 15.5246 8.55378 15.1027L18.9461 9.10268C19.6362 8.70424 20.5967 8.9616 20.9951 9.65172L23.9951 14.8479C24.417 15.5786 24.1362 16.4985 23.4461 16.8969L13.0538 22.8969C12.3231 23.3188 11.4266 23.0786 11.0047 22.3479L8.00475 17.1517Z" fill="#3B3C4A"/>
          </svg>
          <div v-if="panelTitle">
            <h2 class="text-[20px] font-semibold leading-none">{{ panelTitle }}</h2>
            <p class="mt-2 text-[18px] text-dark/75">{{ attractionTitle }}</p>
          </div>
        </div>

        <div :class="bodyClasses">
          <div v-if="loading" :class="feedbackPanelClasses">
            Loading booking options...
          </div>

          <div v-else-if="loadFailed" :class="feedbackPanelClasses">
            <LoadError @retry="loadWidget" />
          </div>

          <div v-else-if="!attraction" :class="feedbackPanelClasses">
            {{ emptyStateMessage }}
          </div>

          <template v-else>
            <div v-if="timeStepTransitionLoading" :class="timeTransitionPanelClasses">
              Loading available times...
            </div>

            <div v-else-if="step === 'date'" :class="dateStepClasses">
            <section v-if="hasProductOptions">
              <div class="mb-4">
                <h3 class="text-base font-semibold">Experience option</h3>
                <p class="text-mdx text-dark/70">Choose which ticketed experience you would like to book.</p>
              </div>

              <div :class="optionPanelClasses">
                <label
                  v-for="option in productOptions"
                  :key="option.product"
                  class="flex cursor-pointer flex-col overflow-hidden rounded-xl border bg-white transition-opacity focus-within:ring-2 focus-within:ring-dark/20"
                  :class="productOptionClasses(option)"
                >
                  <span class="flex items-start justify-between gap-4 p-4">
                    <span class="flex min-w-0 flex-1 flex-col gap-1">
                      <span class="text-base font-semibold">{{ option.title }}</span>
                      <span v-if="option.desc" class="text-mdx text-dark/70">{{ option.desc }}</span>
                    </span>

                    <span class="mt-1 flex h-6 w-6 shrink-0 items-center justify-center rounded-full border border-dark bg-white p-[3px]">
                      <span
                        v-if="option.product === productCode"
                        class="block h-full w-full rounded-full bg-dark"
                      ></span>
                      <input
                        :checked="option.product === productCode"
                        :value="option.product"
                        class="sr-only"
                        name="experience-option"
                        type="radio"
                        @change="selectProductOption(option.product)"
                      >
                    </span>
                  </span>

                  <span
                    v-if="option.banner"
                    class="block bg-primary px-4 py-2 text-sm font-semibold leading-tight text-dark"
                  >
                    {{ option.banner }}
                  </span>
                </label>
              </div>
            </section>

            <CheckoutDateVisitSelector
              :cart-layout="cartLayout"
              class="flex-grow"
              :dates="visibleDates"
              :fullscreen-active="fullscreenActive"
              :selected-date="selectedDate"
              @open-calendar="openCalendar"
              @select-date="selectDate"
            />

            <p
              v-if="dateSelectionError"
              class="text-sm font-semibold text-[#d64545]"
              role="alert"
            >
              {{ dateSelectionError }}
            </p>

            <div v-if="calendarOpen && !fullscreenActive" class="space-y-6">
              <CheckoutCalendarModal
                :open="calendarOpen"
                :title="formatMonth(calendarCursor)"
                :weekdays="weekdays"
                :days="calendarDays"
                :selected-value="calendarSelection"
                @close="closeCalendar"
                @save="saveCalendarDate"
                @previous-month="calendarCursor = calendarCursor.clone().subtract(1, 'month')"
                @next-month="calendarCursor = calendarCursor.clone().add(1, 'month')"
                @select-day="calendarSelection = $event"
              />
            </div>

            <CheckoutStartTimeSelector
              v-if="showInlineTimeSelector"
              :available-times="availableTimes"
              :cart-layout="cartLayout"
              :displayed-times="displayedTimes"
              :fullscreen-active="fullscreenActive"
              :has-extra-times="hasExtraTimes"
              :selected-time="selectedTime"
              :show-all-times="showAllTimes"
              :time-loading="timeLoading"
              :walk-in-only-date="walkInOnlyDate"
              :scarcity-limit="scarcityLimit"
              @select-time="selectTime"
              @toggle-more-times="toggleMoreTimes"
            />

            <div v-if="!fullscreenActionBarVisible" :class="actionBarClasses">
              <div></div>
              <button
                v-if="dateTimeCombined"
                class="button button-secondary inline-flex items-center gap-2 cursor-pointer disabled:opacity-70"
                :disabled="!selectedAvailability"
                @click="goToStep('tickets')"
              >
                <span>Select Tickets</span>
                <StepChevronIcon />
              </button>
              <button
                v-else
                class="button button-secondary inline-flex items-center gap-2 cursor-pointer disabled:opacity-70"
                :disabled="timeStepTransitionLoading"
                @click="continueToTimeSelection"
              >
                <span>{{ timeStepTransitionLoading ? 'Loading times...' : 'Select Time' }}</span>
                <StepChevronIcon />
              </button>
            </div>

            </div>

            <div v-else-if="step === 'time'" :class="looseStepClasses">
            <div class="flex items-start gap-2">
              <div>
                <span class="rounded-full h-6 w-6 flex justify-center items-center bg-[#70D6AF]">
                  <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M13.2065 1.46975C13.5978 1.83095 13.5978 2.46306 13.2065 2.82426L5.50084 10.5299C5.13963 10.9213 4.50752 10.9213 4.14632 10.5299L0.293478 6.67711C-0.0978261 6.3159 -0.0978261 5.68379 0.293478 5.32259C0.654682 4.93129 1.28679 4.93129 1.64799 5.32259L4.83863 8.48313L11.852 1.46975C12.2132 1.07844 12.8453 1.07844 13.2065 1.46975Z" fill="#336552"/>
                  </svg>
                </span>
              </div>
              <div class="flex-grow">
                <div class="text-base font-semibold">{{ formatLongDate(selectedDate) }}</div>
              </div>
              <button class="text-mdx font-semibold text-dark underline underline-offset-4" @click="goToDate">
                Change
              </button>
            </div>

            <CheckoutStartTimeSelector
              class="flex-grow"
              :available-times="availableTimes"
              :cart-layout="cartLayout"
              :displayed-times="displayedTimes"
              :fullscreen-active="fullscreenActive"
              :has-extra-times="hasExtraTimes"
              :selected-time="selectedTime"
              :show-all-times="showAllTimes"
              :time-loading="timeLoading"
              :walk-in-only-date="walkInOnlyDate"
              :scarcity-limit="scarcityLimit"
              @select-time="selectTime"
              @toggle-more-times="toggleMoreTimes"
            />

            <div v-if="!fullscreenActive" :class="actionBarClasses">
              <button class="flex h-14 w-14 items-center justify-center rounded-full bg-white text-[24px]" @click="goToDate">‹</button>
              <button
                class="button button-secondary inline-flex items-center gap-2 cursor-pointer"
                :disabled="!selectedAvailability"
                @click="goToStep('tickets')"
              >
                <span>Select Tickets</span>
                <StepChevronIcon />
              </button>
            </div>
          </div>

          <div v-else-if="step === 'tickets'" :class="stepClasses">
            <div>
              <div class="flex items-start gap-2">
                <div>
                  <span class="rounded-full h-6 w-6 flex justify-center items-center bg-[#70D6AF]">
                    <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.2065 1.46975C13.5978 1.83095 13.5978 2.46306 13.2065 2.82426L5.50084 10.5299C5.13963 10.9213 4.50752 10.9213 4.14632 10.5299L0.293478 6.67711C-0.0978261 6.3159 -0.0978261 5.68379 0.293478 5.32259C0.654682 4.93129 1.28679 4.93129 1.64799 5.32259L4.83863 8.48313L11.852 1.46975C12.2132 1.07844 12.8453 1.07844 13.2065 1.46975Z" fill="#336552"/>
                    </svg>
                  </span>
                </div>
                <div class="flex-grow">
                  <div class="text-base font-semibold">{{ formatLongDate(selectedTime) }}</div>
                  <div class="mt-1 text-mdx text-dark/70">{{ formatTime(selectedTime) }}</div>
                </div>
                <button class="text-mdx font-semibold text-dark underline underline-offset-4" @click="goToTime">
                  Change
                </button>
              </div>
            </div>

            <div class="space-y-4 flex-grow">
              <div v-for="ticket in sortedPriceOptions" :key="ticket.label" :class="ticketRowClasses">
                <div class="flex lg:flex-row items-start lg:items-center gap-4 flex-grow">
                  <div class="flex flex-col lg:flex-row items-start lg:items-center gap-2 w-[min(40%, 200px)]">
                    <div v-if="fullscreenActive" class="flex gap-2">
                      <div :class="ticketLabelClasses">{{ ticket.label }}</div>
                      <div :class="ticketPriceClasses">{{ formatCurrency(ticket.price) }}</div>
                    </div>
                    <div v-else :class="ticketLabelClasses">{{ ticket.label }}</div>
                    <div v-if="ticketDescription(ticket)" :class="ticketDescriptionClasses">{{ ticketDescription(ticket) }}</div>
                  </div>
                  <div :class="ticketPriceClasses" v-if="!fullscreenActive">{{ formatCurrency(ticket.price) }}</div>
                </div>
                <div class="flex items-center gap-4">
                  <button :class="ticketQuantityButtonClasses" v-if="ticketCount(ticket) > 0" @click="adjustTicket(ticket, -1)">-</button>
                  <span :class="ticketCountClasses">{{ ticketCount(ticket) }}</span>
                  <button :class="ticketQuantityButtonClasses" @click="adjustTicket(ticket, 1)">+</button>
                </div>
              </div>

              <p v-if="participantError && !fullscreenActive" class="text-sm text-[#d64545]">{{ participantError }}</p>
            </div>



            <div v-if="!fullscreenActive" :class="actionBarClasses">
              <button class="flex h-14 w-14 items-center justify-center rounded-full bg-white text-[24px]" @click="goToTime">‹</button>
              <button class="button button-secondary inline-flex items-center gap-2 cursor-pointer" @click="continueFromTickets">
                <span>Continue</span>
                <StepChevronIcon />
              </button>
            </div>
          </div>

          <div v-else-if="step === 'extras'" :class="stepClasses">
            <div>
              <h3 class="text-base font-semibold">Add to your experience</h3>
              <p class="mt-2 text-mdx text-dark/70">Choose any optional extras, or continue without them.</p>
            </div>
            <ExtrasOptions v-model="selectedExtras" :extras="attractionExtras" class="flex-grow" />
            <div v-if="!fullscreenActive" :class="reviewActionBarClasses">
              <button :class="navBackButtonClasses" aria-label="Back to tickets" @click="goToStep('tickets')">‹</button>
              <div class="text-right flex-grow">
                <div class="text-xl font-semibold leading-none">{{ formatCurrency(selectionTotal) }}</div>
                <div class="mt-2 text-sm text-dark/60">AUD (inc. GST)</div>
              </div>
              <button class="button button-secondary inline-flex items-center gap-2 cursor-pointer" @click="goToStep('review')">
                <span>Continue</span>
                <StepChevronIcon />
              </button>
            </div>
          </div>

          <div v-else-if="step === 'review'" :class="stepClasses">
            <div class="space-y-4">
              <div class="flex items-start gap-2">
                <div>
                  <span class="rounded-full h-6 w-6 flex justify-center items-center bg-[#70D6AF]">
                    <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.2065 1.46975C13.5978 1.83095 13.5978 2.46306 13.2065 2.82426L5.50084 10.5299C5.13963 10.9213 4.50752 10.9213 4.14632 10.5299L0.293478 6.67711C-0.0978261 6.3159 -0.0978261 5.68379 0.293478 5.32259C0.654682 4.93129 1.28679 4.93129 1.64799 5.32259L4.83863 8.48313L11.852 1.46975C12.2132 1.07844 12.8453 1.07844 13.2065 1.46975Z" fill="#336552"/>
                    </svg>
                  </span>
                </div>
                <div class="flex-grow">
                  <div class="text-base font-semibold">{{ formatLongDate(selectedTime) }}</div>
                  <div class="mt-1 text-mdx text-dark/70">{{ formatTime(selectedTime) }}</div>
                </div>
                <button class="text-mdx font-semibold text-dark underline underline-offset-4" @click="goToTime">
                  Change
                </button>
              </div>
              <div class="flex items-start justify-between gap-2 border-t border-dashed border-primary pt-4">
                <div>
                  <span class="rounded-full h-6 w-6 flex justify-center items-center bg-[#70D6AF]">
                    <svg width="14" height="12" viewBox="0 0 14 12" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M13.2065 1.46975C13.5978 1.83095 13.5978 2.46306 13.2065 2.82426L5.50084 10.5299C5.13963 10.9213 4.50752 10.9213 4.14632 10.5299L0.293478 6.67711C-0.0978261 6.3159 -0.0978261 5.68379 0.293478 5.32259C0.654682 4.93129 1.28679 4.93129 1.64799 5.32259L4.83863 8.48313L11.852 1.46975C12.2132 1.07844 12.8453 1.07844 13.2065 1.46975Z" fill="#336552"/>
                    </svg>
                  </span>
                </div>
                <div class="flex-grow">
                  <div class="text-base font-semibold">{{ participantSummary }}</div>
                </div>
                <button class="text-mdx font-semibold text-dark underline underline-offset-4" @click="goToStep('tickets')">
                  Change
                </button>
              </div>
            </div>

            <div v-if="hasExtras" class="flex items-start justify-between gap-4 border-t border-dashed border-primary pt-4">
              <div class="flex-grow space-y-2">
                <h3 class="text-base font-semibold">Extras</h3>
                <p v-if="!chosenExtras.length" class="text-mdx text-dark/70">No extras selected</p>
                <div v-for="extra in chosenExtras" :key="extra.key" class="flex justify-between gap-4 text-mdx">
                  <span>{{ extra.count }}x {{ extra.label }}</span>
                  <span>{{ formatCurrency(extra.total) }}</span>
                </div>
              </div>
              <button class="text-mdx font-semibold text-dark underline underline-offset-4" @click="goToStep('extras')">Change</button>
            </div>

            <div class="space-y-3">
              <label v-for="requirement in reviewRequirements" :key="requirement.key" class="flex items-start gap-3 text-mdx leading-6">
                <input
                  v-model="acceptedRequirements"
                  :aria-invalid="Boolean(confirmationError)"
                  :value="requirement.key"
                  type="checkbox"
                  class="mt-1 h-5 w-5 border-dark"
                />
                <span>{{ requirement.message }}</span>
              </label>
            </div>

            <p
              v-if="confirmationError"
              class="text-sm font-semibold text-[#d64545]"
              role="alert"
            >
              {{ confirmationError }}
            </p>

            <div v-if="!fullscreenActive" :class="reviewActionBarClasses">
              <button class="flex h-14 w-14 items-center justify-center rounded-full bg-white text-[24px]" @click="goToStep(hasExtras ? 'extras' : 'tickets')">‹</button>
              <div class="text-right flex-grow">
                <div class="text-xl font-semibold leading-none">{{ formatCurrency(selectionTotal) }}</div>
                <div class="mt-2 text-sm text-dark/60">AUD (inc. GST)</div>
              </div>
              <button
                class="button button-secondary inline-flex items-center gap-2 cursor-pointer"
                :disabled="cartSubmissionPending"
                @click="addSelectionToCart"
              >
                <span>{{ submitButtonLabel }}</span>
                <StepChevronIcon />
              </button>
            </div>
          </div>

          <div v-else :class="stepClasses">
            <div class="flex items-start justify-between gap-4 border-b border-dashed border-primary pb-4">
              <div>
                <h3 class="text-xl font-semibold">{{ addedCartTitle }}</h3>
                <p class="mt-2 text-mdx text-dark/70">{{ attractionTitle }}</p>
              </div>
              <a :href="cartUrl" class="text-mdx font-semibold text-dark underline underline-offset-4">View Cart</a>
            </div>

            <div class="space-y-3 text-mdx">
              <div class="flex items-start justify-between gap-4">
                <div>
                  <div>{{ formatLongDate(selectedTime) }}</div>
                  <div class="text-dark/70">{{ formatTime(selectedTime) }}</div>
                </div>
                <button v-if="!isEditingCartItem" class="font-semibold text-dark underline underline-offset-4" @click="removeLastAdded">
                  Remove
                </button>
              </div>

              <div v-for="ticket in chosenTickets" :key="ticket.key" class="flex items-center justify-between gap-4">
                <div>{{ ticket.count }}x {{ ticket.label }}</div>
                <div>{{ formatCurrency(ticket.total) }}</div>
              </div>
              <div v-for="extra in chosenExtras" :key="extra.key" class="flex items-center justify-between gap-4">
                <div>{{ extra.count }}x {{ extra.label }}</div>
                <div>{{ formatCurrency(extra.total) }}</div>
              </div>
            </div>

            <div :class="continueBookingClasses">
              <a :href="continueUrl">Book more experiences</a>
            </div>

            <div v-if="!fullscreenActive" :class="addedActionBarClasses">
              <div>
                <div class="text-2xl font-semibold leading-none">{{ formatCurrency(selectionTotal) }}</div>
                <div class="mt-2 text-md text-dark/60">AUD (inc. GST)</div>
              </div>
              <a :href="cartUrl" class="inline-flex min-w-[200px] button button-secondary transition hover:opacity-90">
                View cart
              </a>
            </div>
            </div>
        </template>
        </div>

        <div v-if="calendarOpen && fullscreenActive" :class="calendarOverlayClasses">
          <div class="flex min-h-full items-center justify-center">
            <div class="w-full min-w-0 max-w-[720px]">
              <CheckoutCalendarModal
                :open="calendarOpen"
                :title="formatMonth(calendarCursor)"
                :weekdays="weekdays"
                :days="calendarDays"
                :selected-value="calendarSelection"
                @close="closeCalendar"
                @save="saveCalendarDate"
                @previous-month="calendarCursor = calendarCursor.clone().subtract(1, 'month')"
                @next-month="calendarCursor = calendarCursor.clone().add(1, 'month')"
                @select-day="calendarSelection = $event"
              />
            </div>
          </div>
        </div>

        <div v-if="fullscreenActionBarVisible" :class="fixedActionBarClasses">
          <template v-if="step === 'date'">
            <button :class="navBackButtonClasses" @click="exitFullscreen">‹</button>
            <button
              class="button button-secondary inline-flex items-center gap-2 cursor-pointer disabled:opacity-70"
              :disabled="timeStepTransitionLoading"
              @click="continueToTimeSelection"
            >
              <span>{{ timeStepTransitionLoading ? 'Loading times...' : 'Select Time' }}</span>
              <StepChevronIcon />
            </button>
          </template>

          <template v-else-if="step === 'time'">
            <button :class="navBackButtonClasses" @click="goToDate">‹</button>
            <button
              class="button button-secondary inline-flex items-center gap-2 cursor-pointer"
              :disabled="!selectedAvailability"
              @click="goToStep('tickets')"
            >
              <span>Select Tickets</span>
              <StepChevronIcon />
            </button>
          </template>

          <template v-else-if="step === 'tickets'">
            <div class="w-full space-y-6">
              <p v-if="participantError" class="text-sm text-[#d64545]">{{ participantError }}</p>
              <div class="flex items-start justify-between gap-4">
                <div class="text-lg font-semibold leading-tight lg:text-xl">Order Subtotal</div>
                <div class="text-right">
                  <div class="text-xl font-semibold leading-tight lg:text-xl">{{ formatCurrency(selectionTotal) }}</div>
                  <div class="mt-1 text-mdx text-dark/70">AUD (inc. GST)</div>
                </div>
              </div>
              <div class="flex items-center justify-between gap-4">
                <button :class="navBackBgButtonClasses" @click="goToTime">‹</button>
                <button class="button button-secondary inline-flex items-center gap-2 cursor-pointer" @click="continueFromTickets">
                  <span>Continue</span>
                  <StepChevronIcon />
                </button>
              </div>
            </div>
          </template>

          <template v-else-if="step === 'extras'">
            <div class="w-full space-y-6">
              <div class="flex items-start justify-between gap-4">
                <div class="text-lg font-semibold leading-tight">Order Subtotal</div>
                <div class="text-right">
                  <div class="text-xl font-semibold leading-tight">{{ formatCurrency(selectionTotal) }}</div>
                  <div class="mt-1 text-mdx text-dark/70">AUD (inc. GST)</div>
                </div>
              </div>
              <div class="flex items-center justify-between gap-4">
                <button :class="navBackBgButtonClasses" aria-label="Back to tickets" @click="goToStep('tickets')">‹</button>
                <button class="button button-secondary inline-flex items-center gap-2 cursor-pointer" @click="goToStep('review')">
                  <span>Continue</span>
                  <StepChevronIcon />
                </button>
              </div>
            </div>
          </template>

          <template v-else-if="step === 'review'">
            <div class="w-full space-y-6">
              <div class="flex items-start justify-between gap-4">
                <div class="text-lg font-semibold leading-tight lg:text-xl">Order Subtotal</div>
                <div class="text-right">
                  <div class="text-xl font-semibold leading-tight lg:text-2xl">{{ formatCurrency(selectionTotal) }}</div>
                  <div class="mt-1 text-mdx text-dark/70">AUD (inc. GST)</div>
                </div>
              </div>
              <p
                v-if="confirmationError"
                class="text-sm font-semibold text-[#d64545]"
                role="alert"
              >
                {{ confirmationError }}
              </p>
              <div class="flex items-center justify-between gap-4">
                <button :class="navBackBgButtonClasses" @click="goToStep(hasExtras ? 'extras' : 'tickets')">‹</button>
                <button
                    class="button button-secondary inline-flex items-center gap-2 cursor-pointer"
                    :disabled="cartSubmissionPending"
                    @click="addSelectionToCart"
                >
                  <span>{{ submitButtonLabel }}</span>
                  <StepChevronIcon />
                </button>
              </div>
            </div>


          </template>

          <template v-else-if="step === 'added'">
            <div>
              <div class="text-[30px] font-semibold leading-none">{{ formatCurrency(selectionTotal) }}</div>
              <div class="mt-2 text-[14px] text-dark/60">AUD (inc. GST)</div>
            </div>
            <a :href="cartUrl" class="inline-flex min-w-[200px] button button-secondary transition hover:opacity-90">
              View cart
            </a>
          </template>
        </div>

      </aside>
    </div>
  </section>
</template>

<script setup>
import {computed, nextTick, onBeforeUnmount, onMounted, ref, watch} from 'vue'
import moment from 'moment'
import api from './api'
import CheckoutCalendarModal from './components/CheckoutCalendarModal.vue'
import CheckoutDateVisitSelector from './components/CheckoutDateVisitSelector.vue'
import CheckoutStartTimeSelector from './components/CheckoutStartTimeSelector.vue'
import ExtrasOptions from './components/ExtrasOptions.vue'
import { useCheckoutCartStore } from './store'
import {
  buildParticipantSummary,
  childTicketNeedsAdultOrSenior,
  childTicketValidationMessage,
  duplicateSessionCartMessage,
  findTicketOption,
  getItemTotal,
  getExtraRows,
  getSeatsUsed,
} from './helpers/cart'
import { confirmationValue, normaliseConfirmationValues } from './helpers/confirmations'
import { formatCurrency, formatLongDate, formatMonth, formatTime, normaliseParticipantKey } from './helpers/format'
import { availabilityRequestDate, parseNextAvailabilityDate } from './helpers/availability'
import { createZohoChatVisibilityController } from './helpers/zoho-chat'
import { StepChevronIcon } from './helpers/step-chevron-icon'
import { disabledDatesForProduct } from '../helpers/disabled-dates'
import { reportLoadError } from '../helpers/network'
import Twist from "@/checkout/components/Twist.vue";
import LoadError from './components/LoadError.vue'
import {trackEvent} from "@/helpers/track";

const props = defineProps({
  code: {
    type: [String, Boolean],
    default: false,
  },
  title: {
    type: String,
    default: '',
  },
  cartUrl: {
    type: String,
    default: '/cart/',
  },
  checkoutUrl: {
    type: String,
    default: '/checkout/',
  },
  continueUrl: {
    type: String,
    default: '/visit-experiences/',
  },
  autoSelectFirst: {
    type: Boolean,
    default: false,
  },
  emptyStateMessage: {
    type: String,
    default: 'This experience is not available for online booking yet.',
  },
  layout: {
    type: String,
    default: 'split',
  },
  widgetId: {
    type: String,
    default: 'booking-form',
  },
  editingItem: {
    type: Object,
    default: null,
  },
})

const emit = defineEmits(['added-to-cart', 'updated-cart', 'fullscreen-mode'])

const store = useCheckoutCartStore()
const loading = ref(true)
const loadFailed = ref(false)
const timeLoading = ref(false)
const timeStepTransitionLoading = ref(false)
const step = ref('date')
const attraction = ref(null)
const fullscreenStarted = ref(false)
const fullscreenViewportAllowed = ref(false)
const dateStart = ref(moment().format('YYYY-MM-DD'))
const selectedDate = ref('')
const selectedProductCode = ref('')
const selectedTime = ref('')
const selectedAvailability = ref(null)
const availableTimes = ref([])
const showAllTimes = ref(false)
const calendarOpen = ref(false)
const calendarCursor = ref(moment())
const calendarSelection = ref('')
const participants = ref({})
const selectedExtras = ref([])
const dateSelectionError = ref('')
const participantError = ref('')
const confirmationError = ref('')
const cartSubmissionPending = ref(false)
const acceptedRequirements = ref([])
const widget = ref(null)

const weekdays = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun']
const fullscreenBreakpointQuery = '(max-width: 1023px)'
const zohoChat = createZohoChatVisibilityController()
let fullscreenBreakpointMedia = null
let fullscreenViewportRaf = null
let fullscreenViewportListenersActive = false
let lockedScrollY = 0
let previousBodyStyles = null

const attractionTitle = computed(() => props.title || attraction.value?.retail_title || attraction.value?.name || 'Book tickets')
const productOptions = computed(() => attraction.value?.rezdy_product_options ?? [])
const scarcityLimit = computed(() => attraction.value?.scarcity_limit ?? 5)
const hasProductOptions = computed(() => productOptions.value.length > 0)
const attractionExtras = computed(() => attraction.value?.extras ?? [])
const hasExtras = computed(() => attractionExtras.value.length > 0)
const isEditingCartItem = computed(() => Boolean(props.editingItem?.id))
const submitButtonLabel = computed(() => isEditingCartItem.value ? 'Update Cart' : 'Add To Cart')
const addedCartTitle = computed(() => isEditingCartItem.value ? 'Updated Cart' : 'Added To Cart')

const panelTitle = computed(() => {
  if (step.value === 'time') return 'Select time'
  if (step.value === 'tickets') return 'Select tickets'
  if (step.value === 'extras') return 'Select extras'
  if (step.value === 'review') return isEditingCartItem.value ? 'Review changes' : 'Review & add to cart'
  if (step.value === 'added') return false; // confirm message is the same
  return isEditingCartItem.value ? 'Edit tickets' : 'Book tickets'
})

const cartLayout = computed(() => props.layout === 'cart')
const fullscreenActionBarEligible = computed(() => (
  (fullscreenStarted.value && step.value === 'date')
  || ['time', 'tickets', 'extras', 'review', 'added'].includes(step.value)
))
const fullscreenActive = computed(() => (
  fullscreenViewportAllowed.value
  && (fullscreenStarted.value || calendarOpen.value || fullscreenActionBarEligible.value)
))
const fullscreenActionBarVisible = computed(() => fullscreenActive.value && fullscreenActionBarEligible.value)
// keep this incase we need it later
// const dateTimeCombined = computed(() => !fullscreenViewportAllowed.value)
const dateTimeCombined = computed(() => false)

const showInlineTimeSelector = computed(() => dateTimeCombined.value && step.value === 'date' && Boolean(selectedDate.value))
const showSlot = computed(() => !cartLayout.value && !fullscreenActive.value)
const sectionClasses = computed(() => {
  if (fullscreenActive.value) {
    return 'booking-fullscreen-frame z-[9999] overflow-hidden bg-white text-dark'
  }

  return cartLayout.value ? 'w-full' : 'mx-auto max-w-[1180px] px-4 py-10 lg:py-16'
})
const layoutClasses = computed(() => {
  if (fullscreenActive.value) {
    return 'mx-auto flex h-full min-w-0 w-full max-w-[1180px] overflow-hidden px-0 py-0 lg:px-8 lg:py-8'
  }

  return cartLayout.value ? 'w-full' : 'grid gap-8 lg:grid-cols-2 lg:items-start'
})
const panelClasses = computed(() => {
  if (fullscreenActive.value) {
    return 'mx-auto flex h-full min-h-0 min-w-0 w-full max-w-[1040px] flex-col overflow-hidden rounded-none bg-white text-dark lg:rounded-[32px]'
  }

  return cartLayout.value
    ? 'rounded-[32px] bg-light p-6 text-dark lg:flex lg:h-full lg:flex-col lg:p-8'
    : 'rounded-2xl bg-light p-6 text-dark h-full lg:flex lg:flex-col'
})
const headerClasses = computed(() => (
  fullscreenActive.value
    ? 'shrink-0 px-6 pb-5 pt-4 text-dark lg:px-10 lg:pt-10'
    : 'mb-6 flex flex-col gap-3 text-dark'
))
const bodyClasses = computed(() => {
  if (!fullscreenActive.value) {
    return 'contents lg:flex lg:min-h-0 lg:flex-1 lg:flex-col'
  }

  const actionBarPadding = ['tickets', 'extras', 'review'].includes(step.value) ? 'pb-48' : 'pb-28'

  return fullscreenActionBarVisible.value
    ? `min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-6 ${actionBarPadding} lg:px-10 lg:pb-0`
    : 'min-h-0 min-w-0 flex-1 overflow-x-hidden overflow-y-auto px-6 pb-4 lg:px-10 lg:pb-0'
})
const dateStepClasses = computed(() => (
  fullscreenActive.value
    ? 'space-y-8'
    : 'space-y-8 lg:flex lg:min-h-0 lg:flex-1 lg:flex-col lg:space-y-0 lg:gap-8'
))
const looseStepClasses = computed(() => (
  fullscreenActive.value
    ? 'flex min-h-full min-w-0 flex-col gap-8 overflow-x-hidden'
    : 'space-y-8 lg:flex lg:min-h-0 lg:flex-1 lg:flex-col lg:space-y-0 lg:gap-8'
))
const stepClasses = computed(() => (
  fullscreenActive.value
    ? 'flex min-h-full min-w-0 flex-col gap-6 overflow-x-hidden'
    : 'space-y-6 lg:flex lg:min-h-0 lg:flex-1 lg:flex-col lg:space-y-0 lg:gap-6'
))
const fixedActionBarClasses = computed(() => {
  const base = 'absolute inset-x-0 bottom-0 z-[10000] mx-auto flex w-full min-w-0 max-w-[1040px] shrink-0 gap-4 overflow-x-hidden border-t border-primary/40 px-6 pb-[calc(1rem+env(safe-area-inset-bottom))] pt-4 lg:static lg:z-20 lg:px-10 lg:pb-4'

  return ['review', 'tickets', 'extras'].includes(step.value)
    ? `${base} flex-col items-stretch bg-light`
    : `${base} items-center justify-between bg-white`
})
const calendarOverlayClasses = 'fixed inset-0 z-[10020] overflow-x-hidden overflow-y-auto bg-dark/30 px-4 py-8'
const cardBackgroundClass = computed(() => (fullscreenActive.value ? 'bg-light' : 'bg-white'))
const feedbackPanelClasses = computed(() => `rounded-3xl ${cardBackgroundClass.value} px-6 py-12 text-center text-dark/70`)
const timeTransitionPanelClasses = computed(() => (
  fullscreenActive.value
    ? 'flex min-h-full items-center justify-center rounded-3xl bg-light px-6 py-12 text-center text-dark/70'
    : feedbackPanelClasses.value
))
const optionPanelClasses = 'flex flex-col gap-3'
const ticketRowClasses = computed(() => (
  fullscreenActive.value
    ? 'flex items-center justify-between gap-4 py-3'
    : `flex items-center justify-between gap-4 rounded-3xl py-4`
))
const ticketLabelClasses = computed(() => (
  fullscreenActive.value ? 'text-xl font-semibold leading-tight' : 'text-lg font-semibold'
))
const ticketPriceClasses = computed(() => (
  fullscreenActive.value ? 'text-lg text-dark/80' : 'text-lg text-dark/70'
))
const ticketDescriptionClasses = computed(() => (
  fullscreenActive.value ? 'text-md text-dark font-light' : 'text-lg text-dark font-light'
))
const ticketQuantityButtonClasses = computed(() => (
  fullscreenActive.value
    ? 'flex h-12 w-12 items-center justify-center rounded-full bg-light text-2xl leading-none'
    : 'flex h-12 w-12 items-center justify-center rounded-full bg-white text-xl'
))
const ticketCountClasses = computed(() => (
  fullscreenActive.value
    ? 'min-w-[28px] text-center text-2xl font-semibold'
    : 'min-w-[20px] text-center text-lg font-semibold'
))
const continueBookingClasses = computed(() => `rounded-full ${cardBackgroundClass.value} px-6 py-4 text-center text-base font-semibold`)
const navBackButtonClasses = computed(() => (
    'flex h-14 w-14 items-center justify-center rounded-full text-xl ' + (fullscreenActive.value ? 'bg-light' : 'bg-white')
))
const navBackBgButtonClasses = computed(() => (
    'flex h-14 w-14 items-center justify-center rounded-full text-xl ' + (fullscreenActive.value ? 'bg-white' : 'bg-light')
))
const actionBarClasses = computed(() => (
  'flex items-center justify-between gap-4 lg:mt-auto lg:pt-6'
))
const reviewActionBarClasses = computed(() => (
  'flex items-center justify-between gap-4 border-t border-dashed border-primary pt-4 lg:mt-auto'
))
const addedActionBarClasses = computed(() => (
  'flex items-end justify-between gap-4 lg:mt-auto lg:pt-6'
))

const productCode = computed(() => selectedProductCode.value || productOptions.value[0]?.product || '')

const disabledDateValues = computed(() => disabledDatesForProduct(
  attraction.value,
  productCode.value
))

const selectedProductOption = computed(() => (
  productOptions.value.find((option) => option.product === productCode.value) ?? null
))

const productOptionClasses = (option) => {
  if (option.product === productCode.value) {
    return 'border-dark opacity-100'
  }

  return selectedProductOption.value?.banner
    ? 'border-dark/10 opacity-50 hover:opacity-75'
    : 'border-dark/10 opacity-100'
}

const numericNoticeMinutes = (value) => {
  if (value === null || value === undefined || value === '') {
    return null
  }

  const minutes = Number(value)
  return Number.isFinite(minutes) ? minutes : null
}

const minimumNoticeMinutes = computed(() => {
  const attractionNotice = numericNoticeMinutes(attraction.value?.cutoff_minutes)
  const optionNotice = numericNoticeMinutes(selectedProductOption.value?.minimumNoticeMinutes)

  return Math.max(attractionNotice ?? 0, optionNotice ?? 0)
})

const earliestBookableMoment = computed(() => moment().add(minimumNoticeMinutes.value, 'minutes'))

const firstVisibleDate = computed(() => {
  const earliest = earliestBookableMoment.value
  const requestedStart = moment(dateStart.value)
  const start = requestedStart.isBefore(earliest, 'day')
    ? earliest.clone()
    : requestedStart.clone()
  const disabledDates = new Set(disabledDateValues.value)

  while (disabledDates.has(start.format('YYYY-MM-DD'))) {
    start.add(1, 'day')
  }

  return start
})

const visibleDates = computed(() => {
  const start = firstVisibleDate.value
  const disabledDates = disabledDateValues.value

  return Array.from({ length: (fullscreenActive.value ? 11 : 11) }, (_, index) => {
    const current = start.clone().add(index, 'day')
    const value = current.format('YYYY-MM-DD');
    return {
      value: value,
      // time: current,
      label: current.format('dddd, Do MMMM YYYY'),
      day: current.isSame(moment(), 'day') ? 'Today' : current.format('ddd'),
      date: current.format('DD'),
      month: current.format('MMM'),
      disabled: disabledDates.includes(value),
    }
  })
})

const clearDateIfBeforeMinimumNotice = () => {
  if (!selectedDate.value || !moment(selectedDate.value).isBefore(earliestBookableMoment.value, 'day')) {
    return
  }

  selectedDate.value = ''
  selectedAvailability.value = null
  selectedTime.value = ''
  availableTimes.value = []
  showAllTimes.value = false
  calendarSelection.value = ''
}

const displayedTimes = computed(() => showAllTimes.value ? availableTimes.value : availableTimes.value.slice(0, 8))
const hasExtraTimes = computed(() => availableTimes.value.length > 8)

const sortedPriceOptions = computed(() => {
  const order = {
    adults: 1,
    children: 2,
    seniors: 3,
    infants: 4,
  }

  return [...(selectedAvailability.value?.priceOptions ?? [])].sort((left, right) => {
    const leftKey = normaliseParticipantKey(left.label)
    const rightKey = normaliseParticipantKey(right.label)
    return (order[leftKey] ?? 99) - (order[rightKey] ?? 99)
  })
})

const selectionItem = computed(() => ({
  attraction: attraction.value?.id,
  availability: selectedAvailability.value,
  participants: participants.value,
  prices: selectedAvailability.value?.priceOptions ?? [],
  extras: selectedExtras.value,
}))

const selectionTotal = computed(() => getItemTotal(selectionItem.value))
const chosenExtras = computed(() => getExtraRows(selectionItem.value))

const participantSummary = computed(() => buildParticipantSummary(participants.value).join(', '))

const chosenTickets = computed(() => buildParticipantSummary(participants.value).map((label) => {
  const [countText, ...nameParts] = label.split(' ')
  const count = Number(countText)
  const name = nameParts.join(' ')
  const option = findTicketOption(selectionItem.value, normaliseParticipantKey(name))
  return {
    key: name,
    label: name,
    count,
    total: count * Number(option?.price ?? 0),
  }
}))

const reviewRequirements = computed(() => {
  const requirements = []
  const ticketTypes = Object.entries(participants.value)
    .filter(([, count]) => Number(count) > 0)
    .map(([key]) => key)

  for (const confirmation of attraction.value?.confirmations ?? []) {
    if (!Array.isArray(confirmation.ticket_types) || confirmation.ticket_types.length === 0) {
      requirements.push({ key: confirmation.message, message: confirmation.message })
      continue
    }

    const matches = confirmation.ticket_types.some((ticketType) => ticketTypes.includes(ticketType))
    if (matches) {
      requirements.push({ key: confirmation.message, message: confirmation.message })
    }
  }

  return requirements
})

const acceptedConfirmationValues = computed(() => normaliseConfirmationValues(
  reviewRequirements.value
    .filter((requirement) => requirementAccepted(requirement))
    .map((requirement) => requirementValue(requirement))
))

const acceptedRequirementValues = computed(() => normaliseConfirmationValues(acceptedRequirements.value))

const requirementValue = (requirement) => confirmationValue(requirement?.key)

const requirementAccepted = (requirement) => acceptedRequirementValues.value.includes(requirementValue(requirement))

const canAddToCart = computed(() => reviewRequirements.value.every((requirement) => (
  requirementAccepted(requirement)
)))

const calendarDays = computed(() => {
  const start = calendarCursor.value.clone().startOf('month').startOf('isoWeek')
  const end = calendarCursor.value.clone().endOf('month').endOf('isoWeek')
  const days = []
  const disabledDates = disabledDateValues.value

  for (const day = start.clone(); day.isSameOrBefore(end, 'day'); day.add(1, 'day')) {
    const value = day.format('YYYY-MM-DD')
    days.push({
      value,
      date: day.clone(),
      disabled: day.isBefore(moment(), 'day') || disabledDates.includes(value),
      muted: !day.isSame(calendarCursor.value, 'month'),
    })
  }

  return days
})

const resetSelectionState = () => {
  step.value = 'date'
  fullscreenStarted.value = false
  dateStart.value = moment().format('YYYY-MM-DD')
  selectedDate.value = ''
  selectedProductCode.value = ''
  selectedTime.value = ''
  selectedAvailability.value = null
  availableTimes.value = []
  showAllTimes.value = false
  calendarOpen.value = false
  calendarCursor.value = moment()
  calendarSelection.value = ''
  participants.value = {}
  selectedExtras.value = []
  dateSelectionError.value = ''
  participantError.value = ''
  confirmationError.value = ''
  acceptedRequirements.value = []
}

const participantCountsForAvailability = (availability, sourceParticipants = participants.value) => {
  return Object.fromEntries((availability?.priceOptions ?? []).map((option) => {
    const key = normaliseParticipantKey(option.label)
    return [key, Number(sourceParticipants?.[key] ?? 0)]
  }))
}

const prepareAvailabilityTime = (time) => {
  if (!time?.startTimeLocal) {
    return time
  }

  return {
    ...time,
    displayTime: time.displayTime ?? moment(time.startTimeLocal).format('h:mmA'),
    hourMinutes: time.hourMinutes ?? moment(time.startTimeLocal).format('h:mm'),
    meridiem: time.meridiem ?? moment(time.startTimeLocal).format('a'),
    disabled: Boolean(time.disabled) || Number(time.seatsAvailable ?? 0) < 1,
  }
}

const hydrateEditingItemState = () => {
  if (!isEditingCartItem.value || !props.editingItem) {
    return
  }

  const item = props.editingItem
  const itemDate = item.time ? moment(item.time).format('YYYY-MM-DD') : ''
  const preparedAvailability = prepareAvailabilityTime(item.availability ?? null)

  selectedProductCode.value = item.product_code || selectedProductCode.value
  selectedDate.value = itemDate
  dateStart.value = itemDate || moment().format('YYYY-MM-DD')
  calendarCursor.value = itemDate ? moment(itemDate) : moment()
  calendarSelection.value = itemDate
  selectedTime.value = item.time ?? ''
  selectedAvailability.value = preparedAvailability
  availableTimes.value = preparedAvailability ? [preparedAvailability] : []
  participants.value = { ...(item.participants ?? {}) }
  selectedExtras.value = (item.extras ?? []).map((extra) => {
    const option = attractionExtras.value.find((candidate) => String(candidate.id) === String(extra.id))
    return {
      ...extra,
      displayName: option?.name ?? extra.displayName ?? extra.name,
      price: Number(option?.price ?? extra.price ?? 0),
    }
  })
  acceptedRequirements.value = normaliseConfirmationValues(item.confirms ?? item.acceptedRequirements ?? [])
}

const resolveAttractionFromCode = (code = false) => {
  return code ? store.getAttractionByCode(code) ?? null : null
}

const applyAttractionSelection = async (code = false) => {
  const nextAttraction = resolveAttractionFromCode(code) ?? (props.autoSelectFirst ? store.attractions[0] ?? null : null)

  resetSelectionState()
  attraction.value = nextAttraction

  if (!nextAttraction) {
    return
  }

  if (props.layout === 'split'){
    trackEvent(`booking-widget-view-${code}`)
  }

  selectedProductCode.value = nextAttraction?.rezdy_product_options?.[0]?.product ?? ''
  hydrateEditingItemState()
}


const goToStep = async (goTo) => {
  if (goTo !== 'date') {
    fullscreenStarted.value = true
  }

  step.value = goTo

  await nextTick();
  await scrollToTop();
}

const openCalendar = async () => {
  fullscreenStarted.value = true
  calendarOpen.value = true
  calendarCursor.value = selectedDate.value
    ? moment(selectedDate.value)
    : firstVisibleDate.value.clone()
  calendarSelection.value = selectedDate.value

  await nextTick()
  await scrollToTop()
}

const closeCalendar = () => {
  calendarOpen.value = false
}

const toggleMoreTimes = () => {
  showAllTimes.value = !showAllTimes.value
}

const scrollToTop = async () => {
  widget.value?.scrollIntoView({
    behavior: "smooth",
    block: "start",
  });
}

const loadAvailability = async () => {
  if (!selectedDate.value || !attraction.value?.id || (hasProductOptions.value && !productCode.value)) {
    availableTimes.value = []
    return
  }

  if (disabledDateValues.value.includes(selectedDate.value)) {
    availableTimes.value = []
    return
  }

  timeLoading.value = true
  selectedAvailability.value = null
  selectedTime.value = ''
  showAllTimes.value = false

  try {
    let dateValue = availabilityRequestDate(selectedDate.value, minimumNoticeMinutes.value)
    let response = await api.availability(attraction.value, productCode.value, dateValue, true)

    if (!Array.isArray(response) && response?.next) {
      const nextDate = parseNextAvailabilityDate(response.next)
      const nextValue = nextDate?.format('YYYY-MM-DD')

      if (nextValue && !disabledDateValues.value.includes(nextValue)) {
        selectedDate.value = nextValue
        dateStart.value = nextValue
        calendarSelection.value = nextValue
        dateValue = availabilityRequestDate(nextDate, minimumNoticeMinutes.value)
        response = await api.availability(attraction.value, productCode.value, dateValue, true)
      }
    }

    const minimum = moment().add(minimumNoticeMinutes.value, 'minutes')
    const items = Array.isArray(response) ? response : []

    availableTimes.value = items
      .filter((item) => moment(item.startTimeLocal).isAfter(minimum))
      .map(prepareAvailabilityTime)
  } finally {
    timeLoading.value = false
  }
}

const walkInOnlyDate = computed(() => {
  const ranges = attraction.value?.walkin_only_dates ?? []

  if (!ranges.length) {
    return false
  }

  if (!selectedDate.value) {
    return false
  }

  const dateToCheck = moment(selectedDate.value)

  return ranges.some((range) => dateToCheck.isBetween(moment(range.start), moment(range.end), 'day', '[]'))
})


const selectDate = async (value) => {
  if (disabledDateValues.value.includes(value)) {
    dateSelectionError.value = 'Please select an available date to continue.'
    return
  }

  const existingParticipants = isEditingCartItem.value ? { ...participants.value } : {}
  const existingRequirements = isEditingCartItem.value ? [...acceptedRequirements.value] : []

  selectedDate.value = value
  dateSelectionError.value = ''
  calendarSelection.value = value
  selectedAvailability.value = null
  selectedTime.value = ''
  availableTimes.value = []
  showAllTimes.value = false
  participants.value = existingParticipants
  participantError.value = ''
  confirmationError.value = ''
  acceptedRequirements.value = existingRequirements

  if (dateTimeCombined.value) {
    await loadAvailability()
    return
  }

  return;

  if (!fullscreenStarted.value){
    await loadAvailability()
    await goToStep('time')
    fullscreenStarted.value = true
  }
}

const continueToTimeSelection = async () => {
  if (!selectedDate.value) {
    dateSelectionError.value = 'Please select a date to continue.'
    return
  }

  if (disabledDateValues.value.includes(selectedDate.value)) {
    dateSelectionError.value = 'Please select an available date to continue.'
    return
  }

  dateSelectionError.value = ''
  fullscreenStarted.value = true
  timeStepTransitionLoading.value = true

  try {
    await nextTick()
    await loadAvailability()
    await goToStep('time')
  } finally {
    timeStepTransitionLoading.value = false
  }
}

const selectProductOption = async (value) => {
  if (!value || value === productCode.value) {
    return
  }

  fullscreenStarted.value = true
  selectedProductCode.value = value
  if (selectedDate.value && disabledDatesForProduct(attraction.value, value).includes(selectedDate.value)) {
    selectedDate.value = ''
    calendarSelection.value = ''
    dateStart.value = moment().format('YYYY-MM-DD')
  }
  participants.value = isEditingCartItem.value ? { ...participants.value } : {}
  dateSelectionError.value = ''
  participantError.value = ''
  confirmationError.value = ''
  acceptedRequirements.value = isEditingCartItem.value ? [...acceptedRequirements.value] : []
  selectedAvailability.value = null
  selectedTime.value = ''
  availableTimes.value = []
}

const selectTime = (time) => {
  if (time.disabled) {
    return
  }

  selectedTime.value = time.startTimeLocal
  selectedAvailability.value = time
  const nextParticipants = participantCountsForAvailability(time)
  const nextSelection = {
    attraction: attraction.value?.id,
    availability: time,
    prices: time.priceOptions ?? [],
    participants: nextParticipants,
  }
  const seatsAvailable = Number(time.seatsAvailable ?? 0)

  participants.value = seatsAvailable > 0 && getSeatsUsed(nextSelection, nextParticipants) > seatsAvailable
    ? participantCountsForAvailability(time, {})
    : nextParticipants
}

const ticketCount = (ticket) => Number(participants.value[normaliseParticipantKey(ticket.label)] ?? 0)

const ticketDescription = (ticket) => {
  const descKey = normaliseParticipantKey(ticket.label)
  return attraction.value?.ticket_desc?.[descKey] ?? ''
}

const adjustTicket = (ticket, amount) => {
  const key = normaliseParticipantKey(ticket.label)
  const current = Number(participants.value[key] ?? 0)
  const next = Math.max(0, current + amount)
  const seatsAvailable = Number(selectedAvailability.value?.seatsAvailable ?? 0)
  const usedSeats = Object.entries(participants.value).reduce((total, [participantKey, count]) => {
    const option = findTicketOption(selectionItem.value, participantKey)
    const seatsUsed = Number(option?.seatsUsed ?? 1)
    const ticketCountValue = participantKey === key ? next : Number(count)
    return total + (ticketCountValue * seatsUsed)
  }, 0)

  if (amount > 0 && seatsAvailable > 0 && usedSeats > seatsAvailable) {
    return
  }

  participants.value = {
    ...participants.value,
    [key]: next,
  }

  participantError.value = ''
}

const continueFromTickets = async () => {
  participantError.value = ''

  const totalParticipants = Object.values(participants.value).reduce((total, count) => total + Number(count), 0)
  if (totalParticipants < 1) {
    participantError.value = 'Select at least one ticket to continue.'
    return
  }

  if (childTicketNeedsAdultOrSenior(selectionItem.value, attraction.value)) {
    participantError.value = childTicketValidationMessage
    return
  }

  acceptedRequirements.value = isEditingCartItem.value ? [...acceptedRequirements.value] : []
  confirmationError.value = ''

  await goToStep(hasExtras.value ? 'extras' : 'review')
}

const goToDate = async () => {
  const existingParticipants = isEditingCartItem.value ? { ...participants.value } : {}
  const existingRequirements = isEditingCartItem.value ? [...acceptedRequirements.value] : []

  selectedAvailability.value = null
  selectedTime.value = ''
  participants.value = existingParticipants
  participantError.value = ''
  confirmationError.value = ''
  acceptedRequirements.value = existingRequirements
  calendarOpen.value = false
  await goToStep('date')
}

const exitFullscreen = async () => {
  calendarOpen.value = false
  fullscreenStarted.value = false
  await goToStep('date')
}

const goToTime = async () => {
  participantError.value = ''
  confirmationError.value = ''
  acceptedRequirements.value = isEditingCartItem.value ? [...acceptedRequirements.value] : []
  await goToStep('time')
}

const validateReviewRequirements = () => {
  if (canAddToCart.value) {
    confirmationError.value = ''
    return true
  }

  confirmationError.value = 'Please confirm all requirements before adding tickets to your cart.'
  return false
}

const addSelectionToCart = async () => {
  if (cartSubmissionPending.value || !validateReviewRequirements()) {
    return
  }

  cartSubmissionPending.value = true

  try {
    const payload = {
      attraction: attraction.value.id,
      attractionTitle: attractionTitle.value,
      product_code: productCode.value,
      time: selectedTime.value,
      participants: participants.value,
      extras: selectedExtras.value.map((extra) => ({ ...extra })),
      availability: selectedAvailability.value,
      prices: selectedAvailability.value?.priceOptions ?? [],
      confirms: acceptedConfirmationValues.value,
    }

    const item = isEditingCartItem.value
      ? store.updateCartItem(props.editingItem.id, payload)
      : store.addToCart(payload)

    if (!item) {
      confirmationError.value = duplicateSessionCartMessage
      return
    }

    await goToStep('added')
    emit(isEditingCartItem.value ? 'updated-cart' : 'added-to-cart', item)
  } finally {
    cartSubmissionPending.value = false
  }
}

const removeLastAdded = () => {
  if (store.lastAddedItemId) {
    store.removeFromCart(store.lastAddedItemId)
  }
  step.value = 'review'
}

const saveCalendarDate = async () => {
  if (!calendarSelection.value) {
    return
  }

  if (disabledDateValues.value.includes(calendarSelection.value)) {
    dateSelectionError.value = 'Please select an available date to continue.'
    return
  }

  calendarOpen.value = false
  dateStart.value = calendarSelection.value;
  await selectDate(calendarSelection.value)

  if (!dateTimeCombined.value) {
    await continueToTimeSelection()
  }
}

const fullscreenViewportStyleTarget = () => (
  typeof document === 'undefined' ? null : document.documentElement
)

const setFullscreenViewportVars = () => {
  if (typeof window === 'undefined') {
    return
  }

  const target = fullscreenViewportStyleTarget()

  if (!target) {
    return
  }

  const viewport = window.visualViewport
  const height = viewport?.height || window.innerHeight
  const width = viewport?.width || window.innerWidth
  const top = viewport?.offsetTop || 0
  const left = viewport?.offsetLeft || 0

  target.style.setProperty('--booking-widget-viewport-height', `${height}px`)
  target.style.setProperty('--booking-widget-viewport-width', `${width}px`)
  target.style.setProperty('--booking-widget-viewport-top', `${top}px`)
  target.style.setProperty('--booking-widget-viewport-left', `${left}px`)
}

const clearFullscreenViewportVars = () => {
  const target = fullscreenViewportStyleTarget()

  if (!target) {
    return
  }

  target.style.removeProperty('--booking-widget-viewport-height')
  target.style.removeProperty('--booking-widget-viewport-width')
  target.style.removeProperty('--booking-widget-viewport-top')
  target.style.removeProperty('--booking-widget-viewport-left')
}

const queueFullscreenViewportUpdate = () => {
  if (typeof window === 'undefined') {
    return
  }

  if (fullscreenViewportRaf) {
    window.cancelAnimationFrame(fullscreenViewportRaf)
  }

  fullscreenViewportRaf = window.requestAnimationFrame(() => {
    fullscreenViewportRaf = null
    setFullscreenViewportVars()
  })
}

const setupFullscreenViewport = () => {
  if (typeof window === 'undefined' || fullscreenViewportListenersActive) {
    return
  }

  fullscreenViewportListenersActive = true
  setFullscreenViewportVars()

  window.addEventListener('resize', queueFullscreenViewportUpdate)
  window.addEventListener('orientationchange', queueFullscreenViewportUpdate)
  window.visualViewport?.addEventListener('resize', queueFullscreenViewportUpdate)
  window.visualViewport?.addEventListener('scroll', queueFullscreenViewportUpdate)
}

const teardownFullscreenViewport = () => {
  if (typeof window === 'undefined') {
    return
  }

  if (fullscreenViewportListenersActive) {
    window.removeEventListener('resize', queueFullscreenViewportUpdate)
    window.removeEventListener('orientationchange', queueFullscreenViewportUpdate)
    window.visualViewport?.removeEventListener('resize', queueFullscreenViewportUpdate)
    window.visualViewport?.removeEventListener('scroll', queueFullscreenViewportUpdate)
  }

  fullscreenViewportListenersActive = false

  if (fullscreenViewportRaf) {
    window.cancelAnimationFrame(fullscreenViewportRaf)
    fullscreenViewportRaf = null
  }

  clearFullscreenViewportVars()
}

const setDocumentScrollLock = (locked) => {
  if (typeof document === 'undefined' || typeof window === 'undefined') {
    return
  }

  if (locked && !previousBodyStyles) {
    lockedScrollY = window.scrollY || window.pageYOffset || 0
    previousBodyStyles = {
      overflow: document.body.style.overflow,
      position: document.body.style.position,
      top: document.body.style.top,
      left: document.body.style.left,
      right: document.body.style.right,
      width: document.body.style.width,
    }

    document.body.classList.add('overflow-hidden')
    document.body.style.overflow = 'hidden'
    document.body.style.position = 'fixed'
    document.body.style.top = `-${lockedScrollY}px`
    document.body.style.left = '0'
    document.body.style.right = '0'
    document.body.style.width = '100%'
    return
  }

  if (!locked && previousBodyStyles) {
    document.body.classList.remove('overflow-hidden')
    document.body.style.overflow = previousBodyStyles.overflow
    document.body.style.position = previousBodyStyles.position
    document.body.style.top = previousBodyStyles.top
    document.body.style.left = previousBodyStyles.left
    document.body.style.right = previousBodyStyles.right
    document.body.style.width = previousBodyStyles.width
    previousBodyStyles = null
    window.scrollTo(0, lockedScrollY)
  }
}

const updateFullscreenViewportAllowed = () => {
  fullscreenViewportAllowed.value = Boolean(fullscreenBreakpointMedia?.matches)
}

const setupFullscreenBreakpoint = () => {
  if (typeof window === 'undefined') {
    return
  }

  fullscreenBreakpointMedia = window.matchMedia(fullscreenBreakpointQuery)
  updateFullscreenViewportAllowed()

  if (typeof fullscreenBreakpointMedia.addEventListener === 'function') {
    fullscreenBreakpointMedia.addEventListener('change', updateFullscreenViewportAllowed)
    return
  }

  fullscreenBreakpointMedia.addListener?.(updateFullscreenViewportAllowed)
}

const teardownFullscreenBreakpoint = () => {
  if (!fullscreenBreakpointMedia) {
    return
  }

  if (typeof fullscreenBreakpointMedia.removeEventListener === 'function') {
    fullscreenBreakpointMedia.removeEventListener('change', updateFullscreenViewportAllowed)
  } else {
    fullscreenBreakpointMedia.removeListener?.(updateFullscreenViewportAllowed)
  }

  fullscreenBreakpointMedia = null
}

watch(() => {
  if (loading.value || loadFailed.value || timeStepTransitionLoading.value || !attraction.value?.code) {
    return null
  }

  return `booking-widget-step-${step.value}-${attraction.value.code}`
}, (event) => {
  if (event) {
    trackEvent(event)
  }
})

watch(calendarOpen, (isOpen) => {
  if (isOpen) {
    calendarCursor.value = selectedDate.value
      ? moment(selectedDate.value)
      : firstVisibleDate.value.clone()
    calendarSelection.value = selectedDate.value
  }
})

watch(dateTimeCombined, async (combined) => {
  if (combined && step.value === 'time') {
    await goToStep('date')
  }
})

watch(canAddToCart, (valid) => {
  if (valid) {
    confirmationError.value = ''
  }
})

watch(minimumNoticeMinutes, clearDateIfBeforeMinimumNotice)

watch(fullscreenActive, (isActive) => {
  emit('fullscreen-mode', isActive)
  if (isActive) {
    setupFullscreenViewport()
  } else {
    teardownFullscreenViewport()
  }
  setDocumentScrollLock(isActive)
  zohoChat.setVisibility(!isActive)
}, { immediate: true })

const loadWidget = async () => {
  loading.value = true
  loadFailed.value = false

  try {
    await store.ensureLoaded()
    await applyAttractionSelection(props.code || false)
  } catch (error) {
    loadFailed.value = true
    reportLoadError(error)
  } finally {
    loading.value = false
  }
}

watch(() => props.code, async (newCode, oldCode) => {
  if (newCode === oldCode) {
    return
  }

  await loadWidget()
})

onMounted(async () => {
  setupFullscreenBreakpoint()

  await loadWidget()
})

onBeforeUnmount(() => {
  teardownFullscreenBreakpoint()
  teardownFullscreenViewport()
  setDocumentScrollLock(false)
  zohoChat.setVisibility(true)
})
</script>
